/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import Counter from "../../../../components/Counter";
import ToastIcon from "../../../../components/ToastIcon";
import { useAppDispatch } from "../../../../store/rootStore";
import { selectCurrentFarmerAccount } from "../../../azure-b2c/storage";
import CartSlice, { selectCurrentCart } from "../../../cart/storage/cart.slice";
import { CartItem } from "../../../cart/storage/cart.types";
import { getUrgentDeliveryUnavailable } from "../../../cart/utils/getUrgentDeliveryUnavailable";
import { Variant } from "../../storage";

import { CartItemCard } from "./CartItemCard";
import { CounterView } from "./ProductCardParent";
import { SideContainer } from "./SideContainer";

export interface DeprecatedCartProductCardProps {
  className?: string;
  item: CartItem;
  onChangeQuantity?: (value: number) => void;
  onChangeVariant?: (value: Variant) => void;
  enableQuantity?: boolean;
  enableVariants?: boolean;
  enableRemove?: boolean;
  handleOpenEditPriceModal?: () => void;
  priceEditable?: boolean;
}

export const DeprecatedCartProductCard: React.FC<DeprecatedCartProductCardProps> = ({
  className,
  item,
  onChangeQuantity,
  onChangeVariant,
  enableQuantity = true,
  enableVariants,
  enableRemove = true,
  handleOpenEditPriceModal,
  priceEditable = false,
}) => {
  const dispatch = useAppDispatch();
  const currentAccount = useSelector(selectCurrentFarmerAccount);

  const cart = useSelector(selectCurrentCart);

  const showUrgentDeliveryUnavailableMessage = React.useMemo(() => {
    if (!currentAccount || !cart) {
      return false;
    }
    return getUrgentDeliveryUnavailable({
      cart,
      productId: item.productId,
      stockAvailabilities: item.stockAvailabilities,
      homeBranchId: currentAccount?.homeBranchId,
    });
  }, [item, cart, currentAccount?.homeBranchId]);

  function onRemove() {
    if (currentAccount) {
      dispatch(
        CartSlice.actions.removeItem({
          accountNumber: currentAccount.accountNumber,
          product: item,
        })
      );
      toast("Item Removed", {
        type: "success",
        icon: <ToastIcon>check_circle</ToastIcon>,
      });
    }
  }

  const renderSideContainer = useMemo(
    () =>
      enableQuantity ? (
        <SideContainer>
          <CounterView>
            <Counter
              value={item.quantity}
              onChange={(value) => {
                if (onChangeQuantity) {
                  onChangeQuantity(value);
                }
              }}
            />
          </CounterView>
        </SideContainer>
      ) : null,
    [item.quantity, onChangeQuantity, enableQuantity]
  );

  return (
    <CartItemCard
      className={className}
      item={item}
      onRemove={enableRemove ? onRemove : undefined}
      onChangeVariant={onChangeVariant}
      enableVariants={enableVariants}
      handleOpenEditPriceModal={handleOpenEditPriceModal}
      priceEditable={priceEditable}
      renderSideContainer={renderSideContainer}
      showUrgentDeliveryUnavailableMessage={showUrgentDeliveryUnavailableMessage}
    />
  );
};
